/**
 * Checkout-optimization billing schemas, previously defined in
 * redo/model/src/checkouts/checkout-optimization-settings.ts. Moved here so
 * the rest of redo/billing/common/ can reference them without depending on
 * redo/model. The settings file re-exports for back-compat.
 */
import { zUsdCents } from "@redotech/money/usd-amounts";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { PaymentProviderType } from "./billing-engine-types";
import {
  BillingPeriod,
  CheckoutOptimizationBillingPeriod,
} from "./service-invoice";

/**
 * Self-serve free-trial product path on chop billing. Extensible for future
 * trial variants. Absent / {@link ConversionBillingTrialType.NONE} means the
 * team is not on unpaid-trial enforcement.
 *
 * Trial end is still {@link baseConversionBillingSchema}'s `startDate`
 * (billing begins then); there is no separate trialEndsAt.
 */
export enum ConversionBillingTrialType {
  NONE = "none",
  INTENT = "intent",
  INTENT_PPU = "intent-ppu",
}

export function isSelfServeBillingTrial(
  trialType: ConversionBillingTrialType | null | undefined,
): boolean {
  return (
    trialType === ConversionBillingTrialType.INTENT ||
    trialType === ConversionBillingTrialType.INTENT_PPU
  );
}

/**
 * Accept both the engine-shared `BillingPeriod.BIMONTHLY` and the legacy
 * `CheckoutOptimizationBillingPeriod.BI_WEEKLY` literal during the cadence
 * alignment migration. Existing team docs store `"bi-weekly"`; new writes
 * use `"bimonthly"`. The transform normalizes both to BIMONTHLY at read
 * time so callers always see the engine-standard value.
 *
 * Once the population-wide migration to `"bimonthly"` is complete, this can
 * be tightened to `z.literal(BillingPeriod.BIMONTHLY)`.
 */
const usagePeriodSchema = z
  .union([
    z.literal(BillingPeriod.BIMONTHLY),
    z.literal(CheckoutOptimizationBillingPeriod.BI_WEEKLY),
  ])
  .transform((): BillingPeriod.BIMONTHLY => BillingPeriod.BIMONTHLY);

const baseConversionBillingSchema = z.object({
  enabled: z.boolean(),
  startDate: z.date().optional(),
  renewOnPeriodEnd: z.boolean(),
  paymentProvider: createEnum(PaymentProviderType, {
    enumName: "PaymentProviderType",
  }).optional(),
  /**
   * Target day-of-month [1, 28] this product should bill on. When set and
   * different from the current invoice end day, the engine generates a
   * prorated transition invoice on the next cycle to land on this day.
   */
  targetBillingDay: z.number().min(1).max(28).optional(),
  /**
   * If true, any order whose checkout ran the Redo intent/hazard system on
   * the storefront becomes billable — even if the shopper never saw the
   * rescue panel and never picked a Redo shipping line. Detected via the
   * `redo_intent_active` cart attribute (scrubbed from the order before the
   * merchant ever sees it).
   */
  billIntentActiveOrders: z.boolean().optional(),
  /**
   * If set, bill the configured percentage (0, 100] of orders attributed to
   * intent. Absence means the percentage-attributed billing mode is off;
   * presence implies it is on at the stored percent.
   */
  billIntentAttributedOrdersPercent: z.number().gt(0).lte(100).optional(),
  /**
   * Self-serve free-trial product path (e.g. activateIntentRescue → intent-ppu).
   * Used by unpaid-trial enforcement; not a presentation lock signal.
   */
  trialType: createEnum(ConversionBillingTrialType, {
    enumName: "ConversionBillingTrialType",
  }).optional(),
});

export const conversionUsageBillingSchema = baseConversionBillingSchema.extend({
  /**
   * For backwards compatibility, we allow the type to be undefined as
   * this field didn't exist in the past and all existing records have a type of "usage".
   */
  billingType: z.literal("usage"),
  period: usagePeriodSchema,
  pricePerOrderInCents: z.number(),
});
export type CheckoutOptimizationUsageBilling = z.infer<
  typeof conversionUsageBillingSchema
>;

export const conversionSubscriptionBillingSchema =
  baseConversionBillingSchema.extend({
    billingType: z.literal("subscription"),
    period: z.literal(BillingPeriod.MONTHLY),
    periodFeeInCents: zUsdCents,
    pricePerOrderInCents: zUsdCents.optional(),
  });
export type CheckoutOptimizationSubscriptionBilling = z.infer<
  typeof conversionSubscriptionBillingSchema
>;

export const checkoutOptimizationBillingSchema = z.union([
  conversionUsageBillingSchema,
  conversionSubscriptionBillingSchema,
]);
export type CheckoutOptimizationBilling = z.infer<
  typeof checkoutOptimizationBillingSchema
>;
